/**
 * Runs the background remover in a Web Worker, falling back to the main
 * thread (after yielding a frame so the "Cutting out…" state can paint) when
 * workers are unavailable. Everything stays on the device.
 */
import { floodCutout, type CutoutResult } from "./cutout";
import type { CutoutRequest, CutoutResponse } from "./cutout.worker";

const WORKER_TIMEOUT_MS = 20_000;
let nextId = 1;

function viaWorker(rgba: Uint8ClampedArray, width: number, height: number): Promise<CutoutResult> {
  return new Promise((resolve, reject) => {
    let worker: Worker;
    try {
      worker = new Worker(new URL("./cutout.worker.ts", import.meta.url), { type: "module" });
    } catch (e) {
      reject(e);
      return;
    }
    const id = nextId++;
    const timer = window.setTimeout(() => {
      worker.terminate();
      reject(new Error("cutout timed out"));
    }, WORKER_TIMEOUT_MS);
    const finish = () => {
      window.clearTimeout(timer);
      worker.terminate();
    };
    worker.onmessage = (e: MessageEvent<CutoutResponse>) => {
      if (e.data.id !== id) return;
      finish();
      if (e.data.rgba && typeof e.data.share === "number") {
        resolve({ rgba: new Uint8ClampedArray(e.data.rgba), share: e.data.share });
      } else {
        reject(new Error(e.data.error ?? "cutout failed"));
      }
    };
    worker.onerror = (e) => {
      finish();
      e.preventDefault();
      reject(new Error("cutout worker failed"));
    };
    // Copy: the caller keeps its original pixels for "Restore".
    const copy = new Uint8ClampedArray(rgba);
    const msg: CutoutRequest = { id, rgba: copy.buffer as ArrayBuffer, width, height };
    worker.postMessage(msg, [msg.rgba]);
  });
}

export async function runCutout(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
): Promise<CutoutResult> {
  if (typeof Worker !== "undefined") {
    try {
      return await viaWorker(rgba, width, height);
    } catch {
      /* fall back to the main thread below */
    }
  }
  await new Promise((r) => setTimeout(r, 16));
  return floodCutout(rgba, width, height);
}
